import { get, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import testIds from "constants/testIds";
import FormSectionLabel from "pages/shipper/RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import {
  DELIVERY_ADDRESS_MATCH_ERROR_PATH,
  validateAddressMatch,
} from "@RequestFormV2/sections/Schedule/validation/validateSchedule";
import { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useRelationalOnChangeValidation } from "@RequestFormV2/hooks/useRelationalOnChangeValidation";
import { stopTestId } from "@RequestFormV2/utils/testIds";
import { useStopAddressExtras } from "@RequestFormV2/sections/Schedule/hooks/useStopAddressExtras";
import { useIsOptionalStopAddress } from "@RequestFormV2/sections/Schedule/hooks/useIsOptionalStopAddress";
import { useStopOptionalAddressTimezoneRestore } from "@RequestFormV2/sections/Schedule/hooks/useStopOptionalAddressTimezoneRestore";
import SharedAddressSection from "@RequestFormV2/sections/Schedule/components/shared/AddressSection";

interface AddressSectionProps {
  stopIndex: number;
  setAddressBookTimezone?: (timezone: string | null | undefined) => void;
  setPortTimezone?: (timezone: string | null | undefined) => void;
}

export default function AddressSection({ stopIndex, setAddressBookTimezone, setPortTimezone }: AddressSectionProps) {
  const intl = useIntl();
  const {
    formState: { errors },
    setError,
  } = useFormContext();
  const { fieldPaths, contactFieldPaths, addressTypeResolver, onAddressSelected, onPortSelected, addressDialog, portLinkedTimezoneRef } =
    useStopAddressExtras(stopIndex, setAddressBookTimezone, setPortTimezone);

  const isOptionalStopAddress = useIsOptionalStopAddress(stopIndex);
  useStopOptionalAddressTimezoneRestore(stopIndex, portLinkedTimezoneRef);

  const stops = useWatch({ name: "schedule.stops" });
  // Address-match only applies to a single pickup/delivery pair; hide it once the
  // request becomes multi-stop even if a stale error lingers in form state.
  const isTwoStopRequest = (stops as ScheduleStopValue[] | undefined)?.length === 2;
  const addressMatchError =
    stopIndex === 1 && isTwoStopRequest ? get(errors, DELIVERY_ADDRESS_MATCH_ERROR_PATH) : undefined;

  useRelationalOnChangeValidation(
    (clear) => validateAddressMatch((stops as ScheduleStopValue[]) ?? [], intl, setError, clear),
    [stops],
    stopIndex === 1,
  );

  return (
    <div className="rfv2-form-section">
      <FormSectionLabel
        label={intl.formatMessage({ id: "general.address", defaultMessage: "Address" })}
        required={!isOptionalStopAddress}
      />

      <SharedAddressSection
        fieldPaths={fieldPaths}
        contactFieldPaths={contactFieldPaths}
        fieldsDisabled={false}
        isRequired
        addressTypeResolver={addressTypeResolver}
        onAddressSelected={onAddressSelected}
        onPortSelected={onPortSelected}
        addressDialog={addressDialog}
        testIdPrefix={stopTestId(testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_ADDRESS_TYPE_PREFIX, stopIndex)}
        portTestId={stopTestId(testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_PORT_SELECT_PREFIX, stopIndex)}
        addressSearchTestId={stopTestId(testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_ADDRESS_SELECT_PREFIX, stopIndex)}
        addressSearchPlaceholder={intl.formatMessage({
          id: "new-request.schedule.multistop.searchAddress",
          defaultMessage: "Select address",
        })}
        addressSelectErrorMessage={
          addressMatchError?.message ? String(addressMatchError.message) : undefined
        }
      />
    </div>
  );
}
